import { useState } from 'react'
import { api, ApiError } from '../lib/api'
import { outbox } from '../lib/outbox'
import { useApi, useConnection, useGeolocation } from '../lib/hooks'
import type { PhotoRef } from '../lib/photos'
import { PhotoButton } from '../ui/PhotoButton'
import { num, shortDate } from '../lib/format'
import {
  Banner, Empty, IconCheck, IconWifiOff, Loading, Panel,
} from '../ui/kit'
import { FieldHeader } from './FieldShell'

/**
 * The second weigh point.
 *
 * A lot is weighed at the farmgate, moves, and is weighed again on arrival. The gap
 * between the two is the shrink — the number that decides whether this business
 * makes money — so it has to be a real second weighing on a real scale, not a copy
 * of the first. This screen never pre-fills the farmgate weight for that reason.
 */

interface Lot {
  id: string; code: string; status: string
  cropName: string; gradeCode: string; gradeId: string
  farmgateKg: number; depotInKg: number | null
  farmerName: string; farmerCode: string
  routeCode: string | null; fifoBatch: string
  createdAt: string; expiringSoon: boolean
}

interface Grade { id: string; code: string; name: string; specText: string }
interface Crop { id: string; name: string; grades: Grade[] }
interface Depot { id: string; code: string; name: string }
interface Scale { id: string; code: string; certNumber: string | null; driftFlag: boolean }
interface Bundle { crops: Crop[]; depots: Depot[]; scales: Scale[] }

export function DepotIn() {
  const { online } = useConnection()
  // Lots weighed at the farmgate and not yet weighed in anywhere.
  const lots = useApi<Lot[]>('/collections/lots?status=open', { cacheKey: 'lots.awaiting-depot' })
  const bundle = useApi<Bundle>('/reference/bundle', { cacheKey: 'reference.bundle' })

  const [open, setOpen] = useState<Lot | null>(null)
  const [done, setDone] = useState<{ code: string; variance: number; queued: boolean } | null>(null)

  const rows = lots.data ?? []

  if (done) {
    return (
      <>
        <FieldHeader />
        <div className="field-greet">
          <h1>Weighed in</h1>
          <p>{done.code}</p>
        </div>
        <div className="field-body">
          <div className="brand-card" style={{ alignItems: 'center', textAlign: 'center' }}>
            <span className="micro">{done.queued ? 'Saved on this phone' : 'In the store'}</span>
            <div className="bc-hero num">
              {done.variance > 0 ? '−' : '+'}{Math.abs(done.variance).toFixed(2)}%
            </div>
            <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--on-brand-dim)' }}>
              {done.variance > 0.5
                ? 'lost on the way from the farm'
                : done.variance < -0.5
                  ? 'heavier than at the farm — check the scale'
                  : 'matches the weight from the farm'}
            </p>
          </div>

          {done.variance > 2 && (
            <Banner tone="warn" title="That is a big difference">
              Somebody will have to say what happened to the missing weight before this collection
              can be closed off.
            </Banner>
          )}

          <button className="btn lg block" onClick={() => { setDone(null); lots.reload() }}>
            Weigh in another
          </button>
        </div>
      </>
    )
  }

  return (
    <>
      <FieldHeader />
      <div className="field-greet">
        <h1>Weigh in</h1>
        <p>Produce arriving at the store. Weigh it again — do not copy the figure from the farm.</p>
      </div>

      <div className="field-body">
        {!online && (
          <Banner tone="warn" icon={<IconWifiOff size={16} />} title="No signal">
            Weigh-ins are held on this phone and sent when you are back on a network.
          </Banner>
        )}

        {lots.loading && <Panel><Loading rows={4} /></Panel>}
        {lots.stale && (
          <Banner tone="info" title="This list may be out of date">
            It was last updated when you had signal. Anything collected since then is not here yet.
          </Banner>
        )}

        {!lots.loading && rows.length === 0 && (
          <Empty title="Nothing waiting">
            Everything collected has reached a store and been weighed.
          </Empty>
        )}

        {rows.length > 0 && (
          <Panel title="Waiting" meta={`${rows.length}`}>
            <div className="panel-body" style={{ paddingTop: 4, paddingBottom: 4 }}>
              {rows.map((l) => (
                <button key={l.id} className="stop-row" style={{ width: '100%' }}
                  onClick={() => setOpen(l)}>
                  <span className="stop-state pending">·</span>
                  <span className="stop-who">
                    <b>{l.cropName} · {num(l.farmgateKg, 1)} kg</b>
                    <i>
                      {l.farmerName} · {l.code}
                      {l.routeCode && ` · ${l.routeCode}`}
                    </i>
                  </span>
                  <span className="stop-amt">
                    <b>{l.gradeCode}</b>
                    <span>{shortDate(l.createdAt)}</span>
                  </span>
                </button>
              ))}
            </div>
          </Panel>
        )}
      </div>

      {open && bundle.data && (
        <WeighIn
          lot={open}
          bundle={bundle.data}
          online={online}
          onClose={() => setOpen(null)}
          onDone={(r) => { setOpen(null); setDone(r); lots.reload() }}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------- the weighing
function WeighIn({ lot, bundle, online, onClose, onDone }: {
  lot: Lot
  bundle: Bundle
  online: boolean
  onClose: () => void
  onDone: (r: { code: string; variance: number; queued: boolean }) => void
}) {
  const [gross, setGross] = useState('')
  const [tare, setTare] = useState('0')
  const [scaleId, setScaleId] = useState(bundle.scales[0]?.id ?? '')
  const [depotId, setDepotId] = useState(bundle.depots[0]?.id ?? '')
  const [regradeId, setRegradeId] = useState('')
  const [temp, setTemp] = useState('')
  const [photo, setPhoto] = useState<PhotoRef | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Depot weighing recorded no position at all until now, so a weigh-in could have
  // happened anywhere and the record could not say otherwise.
  const { coords } = useGeolocation()

  const crop = bundle.crops.find((c) => c.grades.some((g) => g.id === lot.gradeId))
  const scale = bundle.scales.find((s) => s.id === scaleId)

  const netKg = Math.max(0, (Number(gross) || 0) - (Number(tare) || 0))
  const variance = lot.farmgateKg > 0
    ? ((Number(lot.farmgateKg) - netKg) / Number(lot.farmgateKg)) * 100
    : 0
  const heavier = netKg > Number(lot.farmgateKg)

  async function save() {
    if (netKg <= 0) { setError('Put the weight on the scale first.'); return }
    setBusy(true)
    setError(null)

    const clientUuid = crypto.randomUUID()
    const payload = {
      clientUuid,
      lotId: lot.id,
      grossKg: Number(gross),
      tareKg: Number(tare) || 0,
      scaleId: scaleId || null,
      method: 'scale',
      depotId: depotId || null,
      regradeToGradeId: regradeId || null,
      temperatureC: temp ? Number(temp) : null,
      photoUrl: photo,
      lat: coords?.lat ?? null,
      lng: coords?.lng ?? null,
      gpsAccuracyM: coords?.accuracy ?? null,
      notes: null,
    }

    try {
      if (!online) {
        await outbox.add({
          clientUuid,
          opType: 'collection.depot_in',
          payload,
          label: lot.code,
          sublabel: `${lot.cropName} · ${netKg.toFixed(1)} kg in`,
          weightKg: netKg,
        })
        onDone({ code: lot.code, variance, queued: true })
        return
      }
      await api.post(`/collections/lots/${lot.id}/depot-in`, payload)
      onDone({ code: lot.code, variance, queued: false })
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="drawer-scrim" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="drawer">
        <header className="drawer-head">
          <div>
            <span className="micro">Weigh in</span>
            <h3>{lot.code}</h3>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </header>

        <div className="drawer-body">
          {error && <div className="error-text" style={{ marginBottom: 12 }}>{error}</div>}

          <div className="mini-row" style={{ marginBottom: 'var(--sp-7)' }}>
            <div className="mini">
              <i>At the farm</i>
              <b className="num">{num(lot.farmgateKg, 1)} kg</b>
              <span>{lot.farmerName}</span>
            </div>
            <div className="mini">
              <i>Here</i>
              <b className="num">{netKg > 0 ? `${netKg.toFixed(1)} kg` : '—'}</b>
              <span>{lot.cropName} {lot.gradeCode}</span>
            </div>
          </div>

          <div className="field">
            <label htmlFor="di-gross">What the scale says</label>
            <input id="di-gross" className="input n" type="number" step="0.1" inputMode="decimal"
              value={gross} onChange={(e) => setGross(e.target.value)} autoFocus />
          </div>

          <div className="field">
            <label htmlFor="di-tare">Crates and packaging</label>
            <input id="di-tare" className="input n" type="number" step="0.1" inputMode="decimal"
              value={tare} onChange={(e) => setTare(e.target.value)} />
          </div>

          <div className="field">
            <label htmlFor="di-scale">Scale</label>
            <select id="di-scale" className="input" value={scaleId}
              onChange={(e) => setScaleId(e.target.value)}>
              {bundle.scales.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code}{s.certNumber ? ` · ${s.certNumber}` : ''}{s.driftFlag ? ' — DRIFTING' : ''}
                </option>
              ))}
            </select>
          </div>

          {scale?.driftFlag && (
            <Banner tone="crit" title="This scale is not weighing accurately">
              It is being checked. Anything weighed on it is marked, so use a different scale if
              there is one.
            </Banner>
          )}

          <div className="field">
            <label htmlFor="di-depot">Store</label>
            <select id="di-depot" className="input" value={depotId}
              onChange={(e) => setDepotId(e.target.value)}>
              {bundle.depots.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>

          <div className="field">
            <label htmlFor="di-regrade">Change the grade</label>
            <select id="di-regrade" className="input" value={regradeId}
              onChange={(e) => setRegradeId(e.target.value)}>
              <option value="">Keep grade {lot.gradeCode}</option>
              {(crop?.grades ?? []).filter((g) => g.id !== lot.gradeId).map((g) => (
                <option key={g.id} value={g.id}>Down to {g.code} — {g.name}</option>
              ))}
            </select>
            <i className="hint">
              Only if it really did arrive worse than it left. The farmer was already paid on the
              grade agreed at their farm, and this does not change that.
            </i>
          </div>

          <PhotoButton
            kind="depot_scale"
            where={coords}
            value={photo}
            onChange={setPhoto}
            label="Photograph the scale"
          />

          <div className="field">
            <label htmlFor="di-temp">Temperature</label>
            <input id="di-temp" className="input n" type="number" step="0.1" inputMode="decimal"
              placeholder="°C" value={temp} onChange={(e) => setTemp(e.target.value)} />
          </div>

          {netKg > 0 && (
            <div style={{
              marginTop: 'var(--sp-6)',
              background: 'var(--surface-2)',
              border: `1px solid ${Math.abs(variance) > 2 || heavier ? 'var(--warn)' : 'var(--line-2)'}`,
              borderRadius: 'var(--r-md)',
              padding: 'var(--sp-5) var(--sp-6)',
              fontSize: 'var(--fs-sm)',
            }}>
              {heavier ? (
                <>
                  <b className="crit-t">Heavier than when it left the farm</b>
                  <p>
                    {num(netKg - Number(lot.farmgateKg), 1)} kg more than it weighed at the farm.
                    Produce does not gain weight — either the scale is wrong or this is the wrong
                    load. Check before saving.
                  </p>
                </>
              ) : (
                <>
                  <b>{variance.toFixed(2)}% lighter than at the farm</b>
                  <p>
                    {num(Number(lot.farmgateKg) - netKg, 1)} kg lost on the way here.
                    {Math.abs(variance) > 2 && ' Somebody will need to explain a gap this size.'}
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        <footer className="drawer-foot">
          <button type="button" className="btn quiet" onClick={onClose}>Cancel</button>
          <button className="btn" disabled={busy || netKg <= 0} onClick={() => void save()}>
            {busy ? 'Saving…' : online ? <><IconCheck size={15} /> Weigh in</> : 'Queue it'}
          </button>
        </footer>
      </div>
    </div>
  )
}
